import { Form, Modal, Select, Typography, message } from 'antd';
import { Customer } from '@/libs/fleet-api/services/customer';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { assignCustomerFrm, getAllUsers } from '@/libs/fleet-api/services/admin';
import { useState, useEffect } from 'react';

export type AssignFrmModalData = {
	customer: Customer;
};

export const useAssignFrmModal = () => {
	const [modalState, setModalState] = useState<{
		isOpen: boolean;
		data?: AssignFrmModalData;
	}>({ isOpen: false });

	return {
		modal: modalState,
		open: (data: AssignFrmModalData) =>
			setModalState({ isOpen: true, data }),
		close: () => setModalState({ isOpen: false }),
		data: modalState.data,
	};
};

export default function AssignFrmModal({
	modal,
	data,
	onClose,
}: {
	modal: ReturnType<typeof useAssignFrmModal>['modal'];
	data?: AssignFrmModalData;
	onClose: () => void;
}) {
	const [form] = Form.useForm();
	const queryClient = useQueryClient();

	// Fetch all users to populate the FRM dropdown
	const { data: usersData, isLoading: isLoadingUsers } = useQuery({
		queryKey: ['admin-users'],
		queryFn: getAllUsers,
		enabled: modal.isOpen,
	});

	// Filter users to only those with FRM role
	const frmSelectedOptions = usersData?.data
		?.filter((user) => user.group.code.toUpperCase() === 'FRM')
		.map((user) => ({
			label: `${user.fullName} (${user.username})`,
			value: user.userId,
		})) ?? [];

	useEffect(() => {
		if (modal.isOpen && data?.customer) {
			form.resetFields();
		}
	}, [modal.isOpen, data, form]);

	const mutation = useMutation({
		mutationFn: assignCustomerFrm,
		onSuccess: () => {
			message.success('Berhasil assign FRM');
			queryClient.invalidateQueries({ queryKey: ['getAdminCustomers'] });
			onClose();
		},
		onError: (error: Error) => {
			message.error(error?.message || 'Gagal assign FRM');
		},
	});

	const handleSubmit = async () => {
		try {
			const values = await form.validateFields();
			if (!data?.customer?.customerId) return;

			mutation.mutate({
				customerId: data.customer.customerId,
				frmId: values.frmId,
			});
		} catch (error) {
			console.error('Validation failed:', error);
		}
	};

	return (
		<Modal
			title="Assign FRM"
			open={modal.isOpen}
			onOk={handleSubmit}
			onCancel={onClose}
			confirmLoading={mutation.isPending}
			okText="Submit"
			cancelText="Cancel"
		>
			<Typography.Paragraph>
				<strong>Customer ID:</strong> {data?.customer?.customerId} <br />
				<strong>Customer Name:</strong> {data?.customer?.customerName} <br />
				<strong>Current FRM:</strong> {data?.customer?.createdBy ?? '-'}
			</Typography.Paragraph>

			<Form form={form} layout="vertical">
				<Form.Item
					name="frmId"
					label="New FRM"
					rules={[{ required: true, message: 'Silahkan pilih FRM baru' }]}
				>
					<Select
						showSearch
						placeholder="Pilih FRM baru"
						loading={isLoadingUsers}
						options={frmSelectedOptions}
						filterOption={(input, option) =>
							(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
						}
					/>
				</Form.Item>
			</Form>
		</Modal>
	);
}
